/**
 * The secure payment panel for a D2C checkout: header with the amount being charged, the embedded
 * Stripe Checkout (or a preparing / failed state with retry), and the authorization line.
 *
 * Shared by the legacy D2C checkout page and the consumer flow's self-funding checkout step. The
 * amount is always the one the Checkout Session will charge; `access` describes what it buys.
 */
import { Lock } from 'lucide-react';
import { Button } from '@design-system';
import { StripeEmbeddedCheckout } from '@/features/advisor-onboarding';

interface D2cCheckoutPaymentBoxProps {
  clientSecret: string | null;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Formatted Checkout Session amount, or null until the session exists. */
  price: string | null;
  /** What the payment buys, e.g. "365 days of access". */
  access: string;
  noRenewal: boolean;
}

export function D2cCheckoutPaymentBox({
  clientSecret,
  isPending,
  isError,
  onRetry,
  price,
  access,
  noRenewal,
}: D2cCheckoutPaymentBoxProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <Lock className="text-foreground size-5" />
          <span className="font-semibold text-gray-950">Enter payment details</span>
        </div>
        <p className="text-foreground mt-1 text-sm">
          {price && `Your card will be charged a one-time fee of ${price} for ${access}.`}
          {price && noRenewal && ' No automatic renewal or recurring charges.'}
        </p>
      </div>
      <div className="min-h-105 flex flex-col bg-white">
        {isError ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
            <p className="text-foreground font-medium">Something went wrong loading checkout.</p>
            <Button
              variant="outline"
              onClick={onRetry}
            >
              Try again
            </Button>
          </div>
        ) : isPending || !clientSecret ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
            <div className="border-primary h-10 w-10 animate-spin rounded-full border-4 border-t-transparent" />
            <p className="text-foreground font-medium">Preparing secure checkout...</p>
          </div>
        ) : (
          <div className="w-full">
            <StripeEmbeddedCheckout clientSecret={clientSecret} />
          </div>
        )}
      </div>
      <div className="bg-white px-6 py-4">
        <p className="text-foreground text-center text-xs">
          {price &&
            `By completing this purchase, you authorize Waterlily to charge your card a one-time fee of ${price} for ${access}.`}
        </p>
      </div>
    </div>
  );
}
